<template>
  <div v-if="canRead">
    <div class="card col-md-12">
      <div class="card-header" style="margin-bottom: 10px">
        <h4 class="card-title" style="display:inline">
          Plan Name Search
        </h4>
        <button type="button" v-if="canEdit" @click="addPlanName" class="btn btn-fill btn-info btn-add-community pull-right">
          Add Plan Name
        </button>
      </div>
      <div class="card-body form-group row">
        <div class="col-md-6">
          <label>Plan Name</label>
          <el-input type="search"
                    name="search"
                    placeholder="Search"
                    @keyup.enter.native="search"
                    v-model="query.search"
                    v-validate="'minFields'"
                    data-vv-name="search"
                    clearable/>
          <span class="text-danger" v-show="errors.has('search')">
            {{ mainSearchFieldErrorText }}
          </span>
        </div>
        <div class="col-md-4">
          <label>Builder
            <span class="text-danger" v-show="errors.has('search')">
              {{ otherSearchFieldErrorText }}
            </span>
          </label>
          <el-autocomplete
            style="width:100%"
            class="inline-input"
            name="builder"
            ref="builderId"
            clearable
            :debounce="150"
            v-model="selectedBuilderName"
            :fetch-suggestions="findBuilders"
            :trigger-on-focus="false"
            value-key="name"
            placeholder="Builder"
            @blur="verifyBuilderAutocompleteSelected"
            @select="setBuilderAndSearch"
            v-validate="'minFields'"
            data-vv-name="builder">
          </el-autocomplete>
          <small class="text-danger" v-show="errors.has('builder')">
            {{ errors.first('builderId') }}
          </small>
          <small class="text-danger" v-show="showInvalidAutocompleteWarning('builderId')">
            {{ fieldNotValidError('Builder') }}
          </small>
        </div>
        <div class="col-md-2" style="padding-left: 0;">
          <label>Active</label>
          <p-switch v-model="query.active" type="primary" on-text="YES" off-text="NO" style="display:block"></p-switch>
        </div>
        <div class="col-md-12">
          <button type="button" @click="search" class="btn btn-fill btn-info pull-right btn-search">
            Search
          </button>
          <button type="button" @click="clear" class="btn btn-fill btn-info pull-right btn-search">
            Clear
          </button>
        </div>
      </div>
    </div><!-- end card -->
    <div class="card">
      <div class="card-body">
        <div class="col-md-12">
          <div class="pull-right">
            <a @click.prevent="downloadExcel" v-if="hasResults" href="#"><img src="static/img/icon-excel.jpg" title="Export to Excel" /></a>
          </div>
          <el-table class="wrap-whole-words indexed-table-column-width"
                    :data="tableData"
                    :key="tableKey"
                    border
                    style="width: 100%"
                    :max-height="tableHeight(400)">
            <el-table-column
              :width="indexColumnWidth"
              align="center"
              label="#"
              type="index">
            </el-table-column>
            <el-table-column
                label="Plan Name"
                min-width="150"
                sortable
                :sort-method="(a, b) => callStringSort(a._source, b._source, 'name')"
                prop="_source.name">
              <template v-slot="props">
                <span class="invisible">{{ props.row._id}}</span>
                <a href="#" @click.prevent="editPlanName(props.row._source)">{{ props.row._source.name }}</a>
              </template>
            </el-table-column>
            <el-table-column
                label="Number"
                min-width="100"
                sortable
                :sort-method="(a, b) => callStringSort(a._source, b._source, 'number')"
                prop="_source.number">
              <template v-slot="props">
                {{ props.row._source.number }}
              </template>
            </el-table-column>
            <el-table-column
                label="Hand"
                min-width="80"
                sortable
                :sort-method="(a, b) => callStringSort(a._source, b._source, 'hand')"
                prop="_source.hand">
              <template v-slot="props">
                {{ props.row._source.hand }}
              </template>
            </el-table-column>
            <el-table-column
                label="Builder"
                min-width="150" sortable
                :sort-method="(a, b) => callStringSort(a._source, b._source, 'builderName')"
                prop="_source.builderName">
              <template v-slot="props">
                {{ lodash.get(props.row._source, 'builderName') }}
              </template>
            </el-table-column>
            <el-table-column
                label="Form"
                min-width="150"
                prop="_source.fileName">
              <template v-slot="props">
                {{ lodash.get(props.row._source, 'fileName') }}
              </template>
            </el-table-column>
            <el-table-column
              label="Energy Form"
              min-width="150"
              prop="_source.energyPlanName">
              <template v-slot="props">
                {{ lodash.get(props.row._source, 'energyPlanName') }}
              </template>
            </el-table-column>
            <el-table-column
              width="185"
              fixed="right"
              label="Actions">
              <template v-slot="props">
                <p-button type="success" size="sm" icon>
                  <i class="fa fa-edit" @click="editPlanName(props.row._source)"></i>
                </p-button>
                <p-button type="info" :disabled="!props.row._source.fileId" size="sm" icon @click="downloadFile(props.row._source.fileId)">
                  <i class="fa fa-download"></i>
                </p-button>
                <p-button type="info" :disabled="!props.row._source.energyPlanId" size="sm" icon @click="downloadFile(props.row._source.energyPlanId)">
                  <i class="fa fa-download"></i>
                </p-button>
                <p-button type="danger" v-if="canEdit" size="sm" icon @click="confirmDelete(props.row)">
                  <i class="fa fa-times"></i>
                </p-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
        <div class="row" style="padding: 0 14px;">
          <div class="col-sm-6 pagination-info">
            <span class="category">Showing {{total}} entries</span>
          </div>
          <div class="col-sm-6">
            <p-button class="pull-right btn-white transparent" size="sm" icon @click="resultsNoValidate">
            </p-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
  import {mapGetters} from 'vuex'
  import {
    notifySuccess,
    notifyError,
    verifyAutocompleteSelected,
    autocompleteReady,
    setIndexColumnWidth,
    stringSort,
    getTableHeight,
    maxResultsWarning,
    autocompleteTextButNoValue,
    autocompleteTextButNoValueMessage
  } from 'src/utilities/utils'
  import PButton from 'src/components/UIComponents/Button.vue'
  import PSwitch from 'src/components/UIComponents/Switch.vue'
  import Constants from 'src/utilities/constants'
  import lodash from 'lodash'
  import Permissions from 'src/utilities/permissions'
  import {sync} from 'vuex-pathify'
  import {OK, NO_CONTENT} from 'http-status-codes'
  import {minFields} from 'src/utilities/validation'

  export default {
    components: {
      PButton,
      PSwitch
    },
    computed: {
      query: sync('planNames@query'),
      ...mapGetters({
        tableData: 'planNames/results',
        autocompleteBuilders: 'companies/autocompleteResultsBuilder'
      }),
      total() {
        return this.tableData.length
      },
      canRead() {
        return this.$can('read', Permissions.PLAN_NAMES)
      },
      canEdit() {
        return this.$can('edit', Permissions.PLAN_NAMES)
      },
      indexColumnWidth() {
        return setIndexColumnWidth(this.tableData.length)
      },
      hasResults() {
        return this.tableData && this.tableData.length > 0
      },
    },
    data() {
      return {
        lodash,
        toDelete: null,
        selectedBuilderId: null,
        selectedBuilderName: null,
        mainSearchFieldErrorText: Constants.MESSAGES.AT_LEAST_ONE_FIELD_REQUIRED,
        otherSearchFieldErrorText: '*',
        constants: Constants,
        tableKey: 0
      }
    },
    methods: {
      showInvalidAutocompleteWarning(refAndPathName) {
        // $ref has to match prop in store
        return autocompleteTextButNoValue(this, refAndPathName, this.$store.get(`planNames/query@${refAndPathName}`))
      },
      fieldNotValidError(field) {
        return autocompleteTextButNoValueMessage(field)
      },
      async downloadFile(fileId) {
        const fileResponse = await this.$store.dispatch('fetchById', {
          id: fileId,
          endpoint: 'files'
        })

        if (OK === fileResponse.status) {
          const file = fileResponse.data
          await this.$store.dispatch('files/download', {
            name: file.name,
            path: file.path
          })
        }
      },
      clear() {
        this.$validator.pause()
        this.selectedBuilderId = null
        this.selectedBuilderName = null
        this.$store.commit('planNames/resetResults')
        this.$store.commit('planNames/resetQuery')
        setTimeout(this.resumeValidation, 250)
      },
      resumeValidation() {
        this.$validator.resume()
      },
      getError(fieldName) {
        return this.errors.first(fieldName)
      },
      addPlanName() {
        this.$eventHub.$emit('show-modal-3', 'Plan Name', null, '500px')
      },
      editPlanName(planName) {
        this.$eventHub.$emit('show-modal-3', 'Plan Name', {id: planName.id}, '500px')
      },
      tableHeight(buffer) {
        return getTableHeight(buffer)
      },
      async search() {
        if (await this.$validator.validateAll()) {
          const response = await this.$store.dispatch('planNames/fetchElasticSearchResults')
          if (response.hits.total.value === 0) {
            this.$eventHub.$emit('show-modal-1', 'No Results Found', null, '500px')
          }
          if (response.hits.total.value > Constants.ELASTICSEARCH.MAX_SEARCH_RESULTS) {
            maxResultsWarning(this, Constants.ELASTICSEARCH.MAX_SEARCH_RESULTS)
          }
        }
      },
      resultsNoValidate() {
        this.$store.dispatch('planNames/fetchElasticSearchResults')
      },
      findBuilders(text, cb) {
        if (autocompleteReady(text)) {
          this.$store.set('planNames/query@builderId', null)
          this.findCompany(text, Constants.COMPANY.TYPES.BUILDER)
          cb(this.autocompleteBuilders)
        }
      },
      async findCompany(text, type) {
          await this.$store.dispatch('companies/fetchByNameStartsWithAndType', {
            type: type,
            name: text
          })
      },
      setBuilderAndSearch(builder) {
        if (builder) {
          this.selectedBuilderId = builder.id
          this.selectedBuilderName = builder.name
          this.$store.set('planNames/query@builderId', builder.id)
          this.$store.commit('companies/autocompleteResultsBuilder', [])
          this.$validator.reset()
        }
      },
      verifyBuilderAutocompleteSelected() {
        verifyAutocompleteSelected(this, 'planNames/query@builderId', 'builder', this.selectedBuilderName)
      },
      async confirmDelete(planNameSearchResult) {
        const planName = planNameSearchResult._source

        let confirmMsg = ''

        if (planName.name && planName.number && planName.hand) {
          confirmMsg = `${planName.name} ${planName.number} ${planName.hand}`
        } else if (planName.name && planName.hand) {
          confirmMsg = `${planName.name} ${planName.hand}`
        } else if (planName.number && planName.hand) {
          confirmMsg = `${planName.number} ${planName.hand}`
        } else if (planName.name) {
          confirmMsg = planName.name
        }  else if (planName.number) {
          confirmMsg = planName.number
        }

        let message = 'Are you sure you want to delete <strong>' + confirmMsg + '</strong>? This will completely remove the plan name.'
        this.toDelete = planNameSearchResult._id

        const payload = {
          message,
          delete: this.delete,
          showButton: true,
        }
        this.$eventHub.$emit('show-modal-1', 'Confirm Delete', payload, '600px')
      },
      async delete() {
        const response = await this.$store.dispatch('delete', { id: this.toDelete, endpoint: 'plan-names' })
        if (NO_CONTENT === response.status) {
          this.$store.commit('planNames/delete', this.toDelete)
          notifySuccess(this, 'Plan Name Deleted Successfully')
        } else {
          notifyError(this, 'Error Deleting Plan Name')
        }
      },
      callStringSort(a, b, prop) {
        return stringSort(a, b, prop)
      },
      downloadExcel() {
        this.$store.dispatch('planNames/resultsToExcel')
      }
    },
    mounted() {
      minFields('planNames')
    },
    watch: {
      selectedBuilderName: function (builderName) {
        if (!builderName) {
          this.$store.commit('planNames/resetBuilderId')
          this.$validator.reset()
        }
      }
    }
  }
</script>
<style>
  .btn-add-community {
    margin-left: 15px;
  }

  .btn-search {
    margin-bottom: 15px;
  }
</style>
